import type { WindowSummary } from '@core/line-probe';
import { Heading } from '@core/ui';
import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { numberFormat, timeFormat } from './format';
import { levelLabelKey } from './LoadChoice';
import { summariseRun, type SummaryRow } from './runSummary';

const DASH = '–';

interface Line {
    key: string;
    target: string;
    when: string;
    load: string;
    row: SummaryRow;
}

/**
 * The values of spec 4.4: one row per target over the whole run, and every sealed window as it was
 * sent to our servers. The background column appears once any window was measured in a background tab.
 * Memoised, and the window rows exist only while their <details> is open: the page renders on the
 * main thread that timestamps the echoes, so a needless render of hundreds of rows every second would
 * show up as ping and jitter.
 */
export const SummaryTable = memo(function SummaryTable({ targets, windows }: { targets: string[]; windows: WindowSummary[] }) {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const decimal = numberFormat(i18n.language, 1);
    const n = (x: number | null) => (x === null ? DASH : decimal.format(x));
    const triple = (...xs: (number | null)[]) => xs.map(n).join(' / ');
    const seconds = (ms: number) => decimal.format(ms / 1000);
    const hasBackground = windows.some((w) => w.backgroundMs > 0);
    const hasLoad = windows.some((w) => w.loadLevel !== 'off');
    const transport = (x: SummaryRow['transport']) => (x === 'none' ? DASH : x === 'mixed' ? t('TestSuite.Line.Summary.Mixed') : x.toUpperCase());

    const runLines: Line[] = useMemo(
        () =>
            targets.flatMap((target) => {
                const row = summariseRun(windows.filter((w) => w.target === target));
                return row ? [{ key: target, target, when: t('TestSuite.Line.Summary.WholeRun'), load: '', row }] : [];
            }),
        [targets, windows, t]
    );
    const windowLines = (): Line[] => {
        const time = timeFormat(i18n.language);
        const whole = numberFormat(i18n.language);
        return [...windows]
            .sort((a, b) => b.windowStart - a.windowStart || targets.indexOf(a.target) - targets.indexOf(b.target))
            .map((w) => ({
                key: `${w.target}-${w.window}`,
                target: w.target,
                when: time.format(w.windowStart),
                load: w.loadLevel === 'off' ? t(levelLabelKey('off')) : `${t(levelLabelKey(w.loadLevel))} (${whole.format(w.loadTargetKbps)} kbit/s)`,
                row: w
            }));
    };

    const table = (lines: Line[]) => (
        <div className="overflow-x-auto">
            <table className="w-full text-sm text-left whitespace-nowrap">
                <thead>
                    <tr className="border-b border-gray-300">
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.Target')}</th>
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.Window')}</th>
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.Transport')}</th>
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.Sent')}</th>
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.Echoed')}</th>
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.Lost')}</th>
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.Late')}</th>
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.Rtt')}</th>
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.Jitter')}</th>
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.JitterUp')}</th>
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.Silence')}</th>
                        <th className="p-1">{t('TestSuite.Line.Summary.Col.Coverage')}</th>
                        {hasBackground && <th className="p-1">{t('TestSuite.Line.Summary.Col.Background')}</th>}
                        {hasLoad && <th className="p-1">{t('TestSuite.Line.Summary.Col.Load')}</th>}
                        {hasLoad && <th className="p-1">{t('TestSuite.Line.Summary.Col.Upload')}</th>}
                    </tr>
                </thead>
                <tbody>
                    {lines.map(({ key, target, when, load, row }) => (
                        <tr key={key} className="border-b border-gray-200">
                            <td className="p-1">{target}</td>
                            <td className="p-1">{when}</td>
                            <td className="p-1">{transport(row.transport)}</td>
                            <td className="p-1">{n(row.sent)}</td>
                            <td className="p-1">{n(row.echoed)}</td>
                            <td className="p-1">{triple(row.lostUp, row.lostDown, row.lostUnknown)}</td>
                            <td className="p-1">{n(row.late)}</td>
                            <td className="p-1">{triple(row.rttP50, row.rttP95, row.rttMax)}</td>
                            <td className="p-1">{triple(row.jitterP50, row.jitterP95)}</td>
                            <td className="p-1">{triple(row.jitterUpP50, row.jitterUpP95)}</td>
                            <td className="p-1">{n(row.silenceMaxMs)}</td>
                            <td className="p-1">{[row.measuredMs, row.stalledMs, row.blockedMs, row.noSessionMs].map(seconds).join(' / ')}</td>
                            {hasBackground && (
                                <td className="p-1">
                                    {seconds(row.backgroundMs)}
                                    {row.backgroundMs > 0 && (
                                        <span className="ms-2 inline-flex items-center gap-1 text-xs">
                                            <span aria-hidden="true" className="inline-block size-2 rounded-xs bg-accent" />
                                            {t('TestSuite.Line.Legend.Background')}
                                        </span>
                                    )}
                                </td>
                            )}
                            {hasLoad && <td className="p-1">{load}</td>}
                            {hasLoad && <td className="p-1">{triple(row.loadSentKbps, row.loadReceivedKbps)}</td>}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <section aria-labelledby="line-summary">
            <Heading variant="h2" classNames="mb-1">
                <span id="line-summary">{t('TestSuite.Line.Summary.Heading')}</span>
            </Heading>
            <p className="text-sm mb-3">{t('TestSuite.Line.Summary.Intro')}</p>
            {windows.length === 0 ? (
                <p>{t('TestSuite.Line.Summary.Empty')}</p>
            ) : (
                <>
                    {table(runLines)}
                    <details className="mt-4" onToggle={(e) => setOpen(e.currentTarget.open)}>
                        <summary className="cursor-pointer">{t('TestSuite.Line.Summary.Windows', { count: windows.length })}</summary>
                        {open && table(windowLines())}
                    </details>
                </>
            )}
        </section>
    );
});
